import { ReportHeader } from '../../header/report-header';
import React, { useEffect, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { formatDate } from '../../../../utils/date-format';
import { InstallmentsSideBarFilter } from './side-bar/installments-side-bar-filter';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { getReportInstallmentsOperation } from './operations/get-report-installments-operation';
import { payAllInstallmentsOperation } from './operations/pay-all-installments-operation';
import {
  InstallmentReportRow,
  PaymentInstallmentStatus,
} from '../../../../api/types/reports/accountant/installment.type';
import { InstallmentsSideBarFilterType } from './side-bar/installments-side-bar-filter.type';
import { WarningDialog } from '../../../../component/ui/warning-dialog/warning-dialog';

export const ReportInstallments = () => {
  const [data, setData] = useState<InstallmentReportRow[]>();
  const [loading, setLoading] = useState(false);
  const [payAllLoading, setPayAllLoading] = useState(false);
  const [showPayAllWarning, setShowPayAllWarning] = useState(false);

  const [filters, setFilters] = useState<InstallmentsSideBarFilterType>({
    selectedMonth: new Date(),
    paymentStatuses: [],
  });

  const fetchReportData = async (filters: InstallmentsSideBarFilterType) => {
    setFilters(filters);
    await getReportInstallmentsOperation(filters, setData, setLoading);
  };

  const getStatusSeverity = (
    status: PaymentInstallmentStatus,
  ): 'success' | 'warning' | 'danger' | 'info' => {
    switch (status) {
      case PaymentInstallmentStatus.PAID:
        return 'success';
      case PaymentInstallmentStatus.PENDING:
        return 'warning';
      case PaymentInstallmentStatus.FAILED:
      case PaymentInstallmentStatus.CANCELLED:
        return 'danger';
      default:
        return 'warning';
    }
  };

  const getStatusLabel = (status: PaymentInstallmentStatus): string => {
    switch (status) {
      case PaymentInstallmentStatus.PENDING:
        return 'ממתין לתשלום';
      case PaymentInstallmentStatus.PAID:
        return 'שולם';
      case PaymentInstallmentStatus.FAILED:
        return 'נכשל';
      case PaymentInstallmentStatus.CANCELLED:
        return 'בוטל';
      default:
        return 'לא ידוע';
    }
  };

  useEffect(() => {
    fetchReportData(filters);
  }, []);

  const handlePayAllClick = () => {
    setShowPayAllWarning(true);
  };

  const handlePayAllConfirm = async () => {
    const success = await payAllInstallmentsOperation(
      filters.selectedMonth,
      setPayAllLoading,
      () => {
        // Refresh the data after successful payment
        fetchReportData(filters);
      },
    );
    if (success) {
      setShowPayAllWarning(false);
    }
  };

  const getSelectedMonthLabel = (): string => {
    if (!filters.selectedMonth) return '';
    return filters.selectedMonth.toLocaleDateString('he-IL', {
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <ReportHeader
        title="דו״ח הוראות קבע"
        description="דו״ח זה מציג מידע אודות כלל הוראות הקבע באשראי של האירגון. הדו״ח מאפשר מעקב אחר תשלומים חודשיים לפי סטטוס ותאריך."
        metadata={[
          {
            label: 'מטרת הדו״ח',
            value: 'מעקב אחר הוראות קבע וסטטוס התשלומים החודשיים',
          },
          { label: 'תדירות עדכון', value: 'יומי' },
        ]}
        category="accounting"
      />

      <div style={{ display: 'flex', gap: '20px' }}>
        <div
          style={{
            height: 'calc(100vh - 230px)',
            minHeight: '100%',
            width: '300px',
            minWidth: '250px',
          }}
        >
          <InstallmentsSideBarFilter onSearch={fetchReportData} />
        </div>

        {/* Table Section */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          {/* Action Button */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              label="גבה את כל התשלומים"
              icon="pi pi-credit-card"
              className="p-button-success"
              loading={payAllLoading}
              disabled={!filters.selectedMonth || loading}
              onClick={handlePayAllClick}
              tooltip={
                !filters.selectedMonth
                  ? 'יש לבחור חודש כדי לגבות תשלומים'
                  : 'לחץ לגביית כל התשלומים של החודש הנבחר'
              }
              tooltipOptions={{ position: 'top' }}
            />
          </div>

          <DataTable
            value={data}
            rowHover
            loading={loading}
            rowClassName={() => 'cursor-pointer'}
            rowsPerPageOptions={[5, 10, 25, 50]}
            emptyMessage={
              <div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>
            }
            dataKey="installmentPlanId"
            scrollable
            scrollHeight="calc(100vh - 230px)"
            tableStyle={{
              direction: 'rtl',
            }}
            className="installments-table"
          >
            <Column
              field="branchName"
              header="שלוחה"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="firstName"
              header="שם משלם"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) => (
                <label>
                  {rowData.firstName} {rowData.lastName}
                </label>
              )}
            />
            <Column
              field="totalAmount"
              header="סכום כולל"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) => `₪${rowData.totalAmount?.toLocaleString()}`}
            />
            <Column
              field="numberOfInstallments"
              header="מספר תשלומים"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="installmentNumber"
              header="תשלום מספר"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) =>
                `${rowData.installmentNumber} / ${rowData.numberOfInstallments}`
              }
            />
            <Column
              field="amount"
              header="סכום תשלום"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) => `₪${rowData.amount?.toLocaleString()}`}
            />
            <Column
              field="dueDate"
              header="תאריך לתשלום"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) => formatDate(rowData.dueDate)}
            />
            <Column
              field="paidAt"
              header="תאריך תשלום בפועל"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) =>
                rowData.paidAt ? formatDate(rowData.paidAt) : '-'
              }
            />
            <Column
              field="firstPaymentDate"
              header="תאריך תשלום ראשון"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) => formatDate(rowData.firstPaymentDate)}
            />
            <Column
              field="paymentDayOfMonth"
              header="יום חיוב בחודש"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="status"
              header="סטטוס"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) => (
                <Tag
                  style={{ minWidth: '100px' }}
                  value={getStatusLabel(rowData.status)}
                  severity={getStatusSeverity(rowData.status)}
                />
              )}
            />
          </DataTable>
        </div>
      </div>

      {/* Pay All Warning Dialog */}
      <WarningDialog
        header="גביית כל התשלומים"
        isVisible={showPayAllWarning}
        onHide={() => setShowPayAllWarning(false)}
        addIcon={true}
        content={`האם אתה בטוח שברצונך לגבות את כל התשלומים עבור החודש ${getSelectedMonthLabel()}?`}
        dangerousContent="פעולה זו תבצע חיוב בכרטיסי האשראי של כל הלקוחות עם הוראות קבע פעילות לחודש זה."
        smallLetters="לאחר ביצוע הפעולה, לא ניתן יהיה לבטל את החיובים שבוצעו."
        notInformation="שים לב: הפעולה תגבה את כל התשלומים הממתינים בחודש הנבחר. ודא שבחרת את החודש הנכון לפני האישור."
        onConfirm={handlePayAllConfirm}
        confirmLabel="אישור גבייה"
        confirmLoading={payAllLoading}
        cancelLabel="ביטול"
      />
    </div>
  );
};
